import { useTheme, type ThemeMode } from '../lib/theme'
import { Icon, type IconName } from './Icon'

const OPTIONS: { mode: ThemeMode; label: string; icon: IconName; hint: string }[] = [
  { mode: 'light', label: 'Light', icon: 'sun', hint: 'Light theme' },
  { mode: 'dark', label: 'Dark', icon: 'moon', hint: 'Dark theme' },
  { mode: 'system', label: 'System', icon: 'monitor', hint: 'Follow your device setting' },
]

/**
 * Light / Dark / System switch.
 *
 * `compact` is icon-only for the top bar and the sign-in screen; the labelled
 * form is for the settings page, where there is room to say what each does.
 */
export function ThemeSwitcher({ compact = false, className = '' }: { compact?: boolean; className?: string }) {
  const { mode, setMode } = useTheme()

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={`inline-flex rounded-[3px] border overflow-hidden ${className}`}
      style={{ borderColor: 'var(--color-rule-bright)', background: 'var(--color-panel)' }}
    >
      {OPTIONS.map((o, i) => {
        const active = o.mode === mode
        return (
          <button
            key={o.mode}
            type="button"
            role="radio"
            aria-checked={active}
            title={o.hint}
            onClick={() => setMode(o.mode)}
            className={`flex items-center gap-1.5 transition-colors ${compact ? 'h-7 w-8 justify-center' : 'h-8 px-3'}`}
            style={{
              background: active ? 'var(--accent)' : 'transparent',
              color: active ? 'var(--accent-ink)' : 'var(--color-ink-3)',
              borderLeft: i === 0 ? 'none' : '1px solid var(--color-rule)',
            }}
          >
            <Icon name={o.icon} size={14} />
            {!compact && <span className="text-[12px] font-medium">{o.label}</span>}
          </button>
        )
      })}
    </div>
  )
}
